/**
 * İlçe × hizmet landing sayfaları (yalnızca Türkçe).
 *
 * Neden: GSC'de "anadolu yakası güvenlik firması" gibi bölgesel sorgularda gösterim
 * alıyoruz ama o sorguya karşılık gelen bir sayfa yok — ana sayfa 9. sıradan 0 tık
 * alıyordu. Her ilçe için ayrı, gerçekten farklı içerik üretiliyor; şablon doldurup
 * ilçe adı değiştiren "ince sayfa" üretimi bilinçli olarak yapılmıyor.
 *
 * Yeni sayfa eklerken: buraya girdi ekle → sitemap otomatik alır (generate-sitemap.mjs
 * bu dosyayı okur) → check-sitemap.sh içindeki EXPECTED değerini artır.
 */

export type LocalFaq = { q: string; a: string };

export type LocalPage = {
  /** hizmet slug'ı — serviceData'daki slug ile birebir aynı olmalı */
  hizmet: string;
  /** bölge slug'ı — URL: /hizmet/:hizmet/:bolge */
  bolge: string;
  bolgeAdi: string;
  title: string;
  description: string;
  h1: string;
  lede: string;
  /** markdown gövde */
  body: string;
  faqs: LocalFaq[];
};

export const LOCAL_PAGES: LocalPage[] = [
  // ─────────────────────────────── ÖZEL GÜVENLİK ───────────────────────────────
  {
    hizmet: "koruma-ve-guvenlik",
    bolge: "anadolu-yakasi",
    bolgeAdi: "Anadolu Yakası",
    title: "Anadolu Yakası Özel Güvenlik Firması",
    description:
      "Anadolu Yakası'nın 13 ilçesinde 5188 belgeli özel güvenlik hizmeti. Site, plaza, AVM ve fabrika koruması, 7/24 izleme merkezi. Ücretsiz keşif: 0216 222 11 44.",
    h1: "Anadolu Yakası Özel Güvenlik Firması",
    lede:
      "2004'ten bu yana İstanbul Anadolu Yakası'nda özel güvenlik hizmeti veriyoruz. Merkezimiz Ataşehir'de; ekiplerimiz Kadıköy'den Tuzla'ya kadar 13 ilçede görev yapıyor.",
    body: `Anadolu Yakası, güvenlik ihtiyacı açısından tek bir bölge gibi davranmaz. Kadıköy'de cadde üstü perakende ve yoğun yaya trafiği, Ataşehir'de kurumsal plaza lobisi, Ümraniye'de sanayi ve depo sahası, Maltepe'de büyük konut siteleri birbirinden tamamen farklı operasyon kurar. Bu yüzden her projeye tek tip vardiya planıyla değil, sahada yapılan keşifle başlıyoruz.

## Anadolu Yakası'nda hizmet verdiğimiz ilçeler

Kadıköy, Ataşehir, Ümraniye, Üsküdar, Maltepe, Kartal, Pendik, Tuzla, Sancaktepe, Çekmeköy, Beykoz, Sultanbeyli ve Şile. Ataşehir'deki merkezimiz sayesinde bölge içi müdahale ve denetim ziyaretlerimiz kısa sürede yapılabiliyor.

## Neden bölgeye hâkim bir firma fark yaratır?

- **Personel devamlılığı:** Güvenlik görevlisinin evi ile görev yeri arasındaki mesafe, devamsızlık ve sirkülasyonun en büyük sebeplerinden biridir. Bölge içi işe alım yaptığımız için vardiya aksamaları azalır.
- **Müdahale süresi:** Denetim ekibi ve yedek personel aynı yakada olduğunda, ani bir hastalık ya da izin durumunda boşluk oluşmaz.
- **Yerel koşul bilgisi:** Trafik saatleri, okul çıkış yoğunluğu, semt pazarı günleri, sahil etkinlik takvimi — vardiya planını doğrudan etkileyen ayrıntılardır.

## Hizmet kapsamımız

- 5188 sayılı kanun kapsamında silahlı ve silahsız özel güvenlik görevlisi
- Sabit nokta, mobil ve yaya devriye hizmeti
- Site, apartman ve rezidans güvenliği; ziyaretçi ve araç giriş kontrolü
- Plaza, AVM ve iş merkezi lobisi; turnike ve kartlı geçiş operasyonu
- Fabrika, depo ve lojistik tesis güvenliği; sevkiyat kapısı kontrolü
- Etkinlik ve organizasyon güvenliği
- Risk analizi, güvenlik danışmanlığı ve acil durum planlaması

## Operasyon merkezimiz

Kendi 7/24 izleme merkezimizden kamera ve alarm sistemlerini takip ediyoruz. [Elektronik güvenlik sistemleri](/hizmet/elektronik-guvenlik-sistemleri) ile personel hizmetini aynı çatı altında verdiğimizde, bir alarm sinyali doğrudan sahadaki görevliye ulaşır; arada üçüncü bir firma beklemez.

Güvenliğin yanı sıra [genel temizlik](/hizmet/genel-temizlik) ve [tesis yönetimi](/hizmet/tesis-yonetimi) hizmetlerini de tek sözleşme altında sunabiliyoruz.`,
    faqs: [
      {
        q: "Anadolu Yakası'nda hangi ilçelere hizmet veriyorsunuz?",
        a: "Kadıköy, Ataşehir, Ümraniye, Üsküdar, Maltepe, Kartal, Pendik, Tuzla, Sancaktepe, Çekmeköy, Beykoz, Sultanbeyli ve Şile dahil 13 ilçede hizmet veriyoruz. Merkezimiz Ataşehir'de bulunuyor.",
      },
      {
        q: "Özel güvenlik görevlileriniz yasal olarak belgeli mi?",
        a: "Evet. Tüm görevlilerimiz 5188 sayılı Özel Güvenlik Hizmetlerine Dair Kanun kapsamında eğitim almış ve özel güvenlik kimlik kartı sahibidir. Silahlı görev gerektiren projelerde silahlı kimlik kartı olan personel görevlendirilir.",
      },
      {
        q: "Keşif ve teklif ücretli mi?",
        a: "Hayır. Yerinde keşif ve teklif hazırlama ücretsizdir. Keşifte tesisin risk noktaları, gerekli personel sayısı ve vardiya düzeni belirlenir; teklif bu analize göre hazırlanır.",
      },
      {
        q: "Kaç personelden itibaren hizmet veriyorsunuz?",
        a: "Tek görevlilik küçük projelerden yüzlerce kişilik operasyonlara kadar çalışıyoruz. Küçük siteler için vardiya paylaşımlı ekonomik modeller de sunulabiliyor; doğru kurgu keşif sonrası netleşir.",
      },
    ],
  },
  {
    hizmet: "koruma-ve-guvenlik",
    bolge: "kadikoy",
    bolgeAdi: "Kadıköy",
    title: "Kadıköy Özel Güvenlik Firması",
    description:
      "Kadıköy'de site, mağaza, kafe ve iş yeri güvenliği. Bağdat Caddesi ve Moda çevresinde yaya yoğunluğuna göre kurgulanmış devriye. Ücretsiz keşif: 0216 222 11 44.",
    h1: "Kadıköy Özel Güvenlik Hizmetleri",
    lede:
      "Kadıköy, Anadolu Yakası'nın en yoğun yaya trafiğine sahip ilçesi. Buradaki güvenlik operasyonu, kapalı bir sitede kurulan düzenden farklı çalışır.",
    body: `Kadıköy'de güvenlik ihtiyacı büyük ölçüde **kamuya açık, yoğun ve gece geç saatlere uzayan** bir dokudan doğar. Bağdat Caddesi ve çevresindeki cadde üstü mağazalar, Moda ve Caferağa'daki kafe-restoran yoğunluğu, Rıhtım çevresindeki toplu taşıma akışı — hepsi görevlinin sürekli insan trafiği içinde çalışmasını gerektirir.

## Kadıköy'de sık karşılaştığımız ihtiyaçlar

- **Cadde üstü perakende:** Mağaza girişinde caydırıcı ama müşteriyi rahatsız etmeyen bir duruş gerekir. Kapı görevlisinin iletişim dili, burada güvenlik kadar satışın da parçasıdır.
- **Kafe, restoran ve gece işletmeleri:** Kapanış saatleri geç olduğu için vardiya planı standart 08-16-24 düzenine oturmaz; hafta sonu ve tatil günleri ayrı planlanır.
- **Tarihi ve az katlı apartmanlar:** Caferağa ve Moda'daki eski yapılarda kartlı geçiş altyapısı çoğu zaman yoktur. Bu binalarda güvenlik büyük ölçüde personel ve kamera ile kurulur; fiziki tadilat gerektirmeyen çözümler öne çıkar.
- **Otopark ve valet alanları:** Cadde üstü işletmelerde araç teslim-alma noktaları hem güvenlik hem trafik sorunudur; görevlinin yetki sınırı sözleşmede net tanımlanmalıdır.

## Kadıköy için kurduğumuz düzen

Keşifte önce **yoğunluk haritası** çıkarıyoruz: hangi saatte hangi kapı, hangi cephe kalabalıklaşıyor. Devriye rotası bu haritaya göre kuruluyor — sabit bir tur değil, saate göre değişen bir tur. Cadde üstü işletmelerde görevlinin dış cephe görüş açısı ile kamera kör noktaları birlikte planlanıyor.

Gece işletmelerinde en kritik konu **olay kaydı**: tutulan tutanak, hem sigorta hem hukuki süreçte belirleyici oluyor. Görevlilerimiz olay bildirimini dijital forma giriyor, kayıt aynı gün yönetime iletiliyor.

## Sitelerde ziyaretçi yönetimi

Kadıköy'ün kapalı sitelerinde en sık gelen şikâyet, kargo ve yemek teslimatının kontrolsüz akışı. Ziyaretçi kaydı, teslimat noktası tanımı ve site sakinine bildirim akışını [tesis yönetimi](/hizmet/tesis-yonetimi) hizmetiyle birlikte kurguladığımızda giriş-çıkış trafiği belirgin şekilde düzeliyor.`,
    faqs: [
      {
        q: "Kadıköy'de mağaza ve kafe için tek görevli yeterli olur mu?",
        a: "Cadde üstü küçük işletmelerde çoğunlukla tek görevli yeterlidir; belirleyici olan kapı sayısı, kapanış saati ve kasa/stok alanının konumu. Kapanış saati 24.00'ü geçen işletmelerde vardiya devri için ikinci kişi gündeme gelir. Kesin sayı ücretsiz keşifte belirlenir.",
      },
      {
        q: "Hafta sonu veya sadece belirli saatler için güvenlik alabilir miyiz?",
        a: "Evet. Kadıköy'de özellikle perakende ve yeme-içme işletmelerinde kısmi zamanlı ve hafta sonu ağırlıklı modeller yaygın olarak uygulanıyor. Sözleşme, ihtiyaç duyulan saat aralığına göre düzenlenir.",
      },
      {
        q: "Eski binamızda kartlı geçiş altyapısı yok, ne öneriyorsunuz?",
        a: "Tarihi ve az katlı yapılarda tadilat gerektirmeyen çözümlerle başlıyoruz: kablosuz kamera, kapı zili/interkom entegrasyonu ve personel destekli ziyaretçi kaydı. Yapı izin veriyorsa kartlı geçiş sonraki aşamada eklenebilir.",
      },
    ],
  },
  {
    hizmet: "koruma-ve-guvenlik",
    bolge: "atasehir",
    bolgeAdi: "Ataşehir",
    title: "Ataşehir Özel Güvenlik Firması",
    description:
      "Ataşehir'de plaza, ofis ve rezidans güvenliği. Finans Merkezi çevresinde kurumsal lobi ve kartlı geçiş operasyonu. Merkezimiz Ataşehir'de: 0216 222 11 44.",
    h1: "Ataşehir Özel Güvenlik Hizmetleri",
    lede:
      "Merkezimiz Ataşehir'de. Bu ilçedeki projelerimizde denetim ekibimiz ve yedek personelimiz sahaya en kısa sürede ulaşıyor.",
    body: `Ataşehir, Anadolu Yakası'nın kurumsal merkezi. İstanbul Finans Merkezi ve Batı Ataşehir'deki plaza yoğunluğu, buradaki güvenlik hizmetini büyük ölçüde **kurumsal lobi operasyonuna** dönüştürüyor. Doğu Ataşehir ve Küçükbakkalköy tarafında ise yeni rezidans ve kapalı site güvenliği ağırlık kazanıyor.

## Plaza ve ofis güvenliğinde belirleyici olan ne?

Plaza güvenliği, klasik "kapıda bekleyen görevli" işi değildir. Belirleyici olanlar:

- **Çok kiracılı yapı:** Aynı binada birden fazla şirket varsa, ziyaretçi yönlendirme ve yetki seviyeleri katman katman tanımlanmalıdır. Bir kiracının misafiri diğerinin katına erişememelidir.
- **Turnike ve kartlı geçiş operasyonu:** Sistem kurulu olsa bile işletilmesi gerekir; kart kaybı, geçici kart, misafir kartı ve arıza anındaki manuel prosedür yazılı olmalıdır.
- **Kurumsal temsil:** Plaza lobisindeki görevli, şirketin ilk temas noktasıdır. Kılık kıyafet, hitap dili ve yabancı dil ihtiyacı sözleşmede tanımlanır.
- **Mesai dışı ve hafta sonu erişimi:** Ofis çalışanlarının mesai dışı girişleri kayıt altına alınmalı, temizlik ve teknik servis ekiplerinin erişimi ayrı planlanmalıdır.

## Rezidans ve site tarafı

Doğu Ataşehir'deki yeni konut projelerinde en sık kurduğumuz düzen: **araç giriş kontrolü + yaya lobi + gece devriyesi** üçlüsü. Otopark katları, çocuk oyun alanları ve sosyal tesis çevresi devriye rotasının sabit durakları oluyor.

## Merkez ilçe olmanın pratik faydası

Ataşehir'deki merkezimiz sayesinde bu ilçedeki projelerde:

- Denetim ziyaretleri daha sık yapılabiliyor
- Ani personel boşluklarında yedek görevli hızlı yönlendiriliyor
- Sözleşme, evrak ve operasyon toplantıları için müşteri ofise kolay ulaşıyor

Plaza projelerinde güvenlikle birlikte [genel temizlik](/hizmet/genel-temizlik) ve [tesis yönetimi](/hizmet/tesis-yonetimi) hizmetini tek sözleşmede birleştirmek, yönetim tarafında muhatap sayısını azaltıyor.`,
    faqs: [
      {
        q: "Plaza lobisi için yabancı dil bilen güvenlik görevlisi sağlıyor musunuz?",
        a: "Evet. Kurumsal lobi projelerinde İngilizce iletişim kurabilen görevli talebi sık geliyor ve karşılanabiliyor. Dil seviyesi ve kaç görevlinin bu niteliğe sahip olacağı sözleşmede tanımlanır.",
      },
      {
        q: "Mevcut turnike ve kartlı geçiş sistemimizi siz mi işleteceksiniz?",
        a: "Evet, mevcut sistemin işletilmesi hizmete dahil edilebilir: kart tanımlama, misafir kartı, kayıp kart prosedürü ve arıza anındaki manuel giriş akışı yazılı prosedüre bağlanır. Sistem yenilemesi gerekiyorsa elektronik güvenlik tarafımız devreye girer.",
      },
      {
        q: "Ataşehir'de ofisiniz olduğu için fiyat avantajı oluyor mu?",
        a: "Doğrudan bir indirim kalemi değil ama operasyonel maliyetimiz düştüğü için tekliflerimiz bölge içinde genelde daha rekabetçi çıkıyor. Asıl fark hızda: denetim ve yedek personel aynı ilçeden geliyor.",
      },
    ],
  },
  {
    hizmet: "koruma-ve-guvenlik",
    bolge: "umraniye",
    bolgeAdi: "Ümraniye",
    title: "Ümraniye Özel Güvenlik Firması",
    description:
      "Ümraniye'de fabrika, depo ve site güvenliği. Dudullu OSB çevresinde sevkiyat kapısı ve araç giriş kontrolü. Ücretsiz keşif: 0216 222 11 44.",
    h1: "Ümraniye Özel Güvenlik Hizmetleri",
    lede:
      "Ümraniye, yoğun konut dokusunun yanı sıra Dudullu Organize Sanayi Bölgesi ile Anadolu Yakası'nın en önemli üretim ve lojistik merkezlerinden biri.",
    body: `Ümraniye'de iki farklı güvenlik dünyası yan yana duruyor: bir tarafta kalabalık konut siteleri, diğer tarafta OSB içindeki fabrikalar ve depolar. İkisinin operasyon mantığı birbirine benzemiyor.

## Sanayi ve depo tesislerinde kritik noktalar

- **Sevkiyat kapısı:** En yüksek risk burada. Giren-çıkan aracın plakası, sürücüsü, irsaliyesi ve dorse mührü kayıt altına alınmadığında stok kaybının kaynağını bulmak imkânsızlaşır.
- **Kantar ve yük kontrolü:** Tesiste kantar varsa, güvenlik görevlisinin yetkisi ve tartım kaydına erişimi net tanımlanmalıdır.
- **Vardiya devri:** Üretim 3 vardiya çalışıyorsa güvenlik de aynı ritimde döner; vardiya devir tutanağı olmadan sorumluluk zinciri kopar.
- **Yüklenici ve taşeron girişi:** Bakım ekipleri, kaynakçılar, tadilat firmaları — geçici personelin kimlik kaydı ve iş güvenliği ekipmanı kontrolü güvenliğin görev tanımına girer.
- **Yangın ve acil durum:** Depolarda yangın riski yüksektir; görevlinin tahliye planındaki rolü ve yangın tüpü kontrol turu yazılı olmalıdır.

## Konut siteleri tarafı

Ümraniye'nin site projelerinde en sık gelen talep, **araç trafiğinin kontrolü**. Otopark kapasitesinin üstünde araç bulunan sitelerde misafir aracı, kargo aracı ve servis aracı akışı ayrıştırılmadığında kapıda tıkanma yaşanıyor. Giriş kontrolünü plaka tanıma sistemiyle destekleyip, görevliyi kapıda kâğıt doldurmaktan kurtarmak çoğu projede tek başına belirgin fark yaratıyor.

## Depo ve fabrikada personel profili

Sanayi tesislerinde görevlendirdiğimiz personelde iş güvenliği eğitimi, vardiyalı çalışma alışkanlığı ve fiziksel dayanıklılık öne çıkar. Bölge içinden işe alım yaptığımız için gece vardiyasında ulaşım kaynaklı devamsızlık sorunu belirgin şekilde azalıyor.

Tesis genelinde [ilaçlama](/hizmet/ilaclama) ve [genel temizlik](/hizmet/genel-temizlik) hizmetleri de aynı sözleşmeye eklenebiliyor; üretim alanlarında bu üç hizmetin takvimi birbiriyle çakışmayacak şekilde planlanıyor.`,
    faqs: [
      {
        q: "Fabrikamızda 3 vardiya üretim var, güvenlik de 7/24 olabilir mi?",
        a: "Evet. Üretim ritmine uyumlu 3 vardiyalı güvenlik düzeni kuruyoruz. Vardiya devir tutanağı, kapı kayıtları ve devriye turları dijital olarak kayıt altına alınır ve yönetime periyodik raporlanır.",
      },
      {
        q: "Sevkiyat kapısında araç ve yük kontrolünü siz mi yapacaksınız?",
        a: "Evet, sözleşmede tanımlandığı ölçüde. Plaka ve sürücü kaydı, irsaliye kontrolü, mühür kontrolü ve gerektiğinde kantar kaydıyla eşleştirme yapılabilir. Görevlinin yetki sınırı ve hangi durumda yönetime bildirim yapacağı yazılı prosedüre bağlanır.",
      },
      {
        q: "Dudullu OSB içinde referansınız var mı?",
        a: "OSB ve çevresindeki sanayi tesislerinde deneyimimiz var. Müşteri isimlerini gizlilik sözleşmeleri nedeniyle paylaşmıyoruz; görüşme sırasında benzer ölçekteki projelerin operasyon kurgusunu detaylı anlatabiliyoruz.",
      },
    ],
  },
  {
    hizmet: "koruma-ve-guvenlik",
    bolge: "maltepe",
    bolgeAdi: "Maltepe",
    title: "Maltepe Özel Güvenlik Firması",
    description:
      "Maltepe'de site, okul ve etkinlik güvenliği. Sahil etkinlik alanı çevresinde kalabalık yönetimi deneyimi. Ücretsiz keşif: 0216 222 11 44.",
    h1: "Maltepe Özel Güvenlik Hizmetleri",
    lede:
      "Maltepe, geniş konut siteleri ile İstanbul'un en büyük açık etkinlik alanlarından birini aynı ilçede barındırıyor. İkisi de güvenlik açısından ayrı uzmanlık istiyor.",
    body: `Maltepe'de iki tip proje öne çıkıyor: **çok bloklu büyük konut siteleri** ve **sahil bandındaki etkinlik/kalabalık organizasyonları**.

## Büyük sitelerde devriye tasarımı

Çok bloklu sitelerde en sık yapılan hata, tek bir görevliye hem kapı hem devriye görevini birlikte vermek. Kapıdan ayrılan görevli olduğunda giriş kontrolsüz kalır; kapıda kalan görevli olduğunda blok araları hiç görülmez. Doğru kurgu genellikle şudur:

- Kapıda sabit nokta (araç ve yaya girişi)
- Bağımsız devriye görevlisi (blok araları, otopark katları, sosyal tesis, çocuk parkı)
- Gece saatlerinde devriye sıklığının artırılması, gündüz azaltılması

Site büyüdükçe devriyenin **tur süresi** kritik hale gelir: 40 dakikayı aşan turlarda aynı noktaya dönüş aralığı güvenlik hissini zayıflatır. Keşifte tur süresini ölçüp buna göre görevli sayısı öneriyoruz.

## Sahil ve etkinlik güvenliği

Maltepe sahilindeki etkinlik alanı, kalabalık yönetimi açısından ayrı bir disiplin gerektirir: giriş-çıkış kapılarının ayrıştırılması, acil tahliye koridorlarının açık tutulması, sahne/alan çevresinde bariyer düzeni ve arama noktalarının kuyruk oluşturmayacak şekilde konumlandırılması.

Etkinlik güvenliğinde ekip sayısı kadar **brifing kalitesi** belirleyicidir. Görev öncesi tüm ekibe alan krokisi, acil durum toplanma noktası ve yetki sınırları anlatılmadan sahaya çıkılmaz.

## Okul ve eğitim kurumları

Maltepe'deki okul ve yurt projelerinde öne çıkan konu, **veli ve öğrenci giriş-çıkış saatlerindeki yoğunluk**. Servis araçlarının park düzeni, yaya geçişi ve okul çıkışında toplanan kalabalık, ayrı bir trafik planı gerektirir.

Site projelerinde güvenlikle birlikte [genel temizlik](/hizmet/genel-temizlik) ve [tesis yönetimi](/hizmet/tesis-yonetimi) hizmetlerini tek sözleşmede sunabiliyoruz.`,
    faqs: [
      {
        q: "Tek seferlik bir etkinlik için güvenlik ekibi alabilir miyiz?",
        a: "Evet. Konser, festival, açılış, kurumsal etkinlik ve spor organizasyonları için proje bazlı ekip kuruyoruz. Ekip sayısı; alan büyüklüğü, beklenen katılımcı sayısı ve kapı sayısına göre keşifte belirlenir.",
      },
      {
        q: "Sitemiz çok bloklu, kaç güvenlik görevlisi gerekir?",
        a: "Belirleyici olan blok sayısından çok devriye tur süresi ve giriş noktası sayısıdır. Keşifte turu bizzat yürüyerek ölçüyoruz; hedefimiz aynı noktaya dönüş aralığının makul kalması. Bu ölçüm sonrası net sayı ve vardiya planı sunuluyor.",
      },
      {
        q: "Gece ve gündüz farklı sayıda personel çalıştırılabilir mi?",
        a: "Evet, çoğu sitede önerdiğimiz model bu. Gündüz kapı ağırlıklı, gece devriye ağırlıklı bir düzen hem güvenliği artırır hem maliyeti dengeler.",
      },
    ],
  },
  {
    hizmet: "koruma-ve-guvenlik",
    bolge: "kartal",
    bolgeAdi: "Kartal",
    title: "Kartal Özel Güvenlik Firması",
    description:
      "Kartal'da rezidans, şantiye ve iş yeri güvenliği. Kentsel dönüşüm projelerinde şantiye ve teslim sonrası site güvenliği. Ücretsiz keşif: 0216 222 11 44.",
    h1: "Kartal Özel Güvenlik Hizmetleri",
    lede:
      "Kartal, kentsel dönüşümün en yoğun yaşandığı ilçelerden biri. Bu da güvenlik ihtiyacını iki aşamalı hale getiriyor: önce şantiye, sonra teslim edilmiş site.",
    body: `Kartal'da bir projenin güvenlik ihtiyacı, inşaat aşamasından oturuma geçişe kadar tamamen değişir. Bu geçişi planlamadan yapılan sözleşmeler, teslim döneminde boşluk yaratır.

## Şantiye güvenliği

İnşaat sahalarında risk profili nettir ve büyük ölçüde **malzeme kaybı ve yetkisiz giriş** üzerine kuruludur:

- Demir, kablo, bakır ve kalıp malzemesi hırsızlığı — özellikle hafta sonu ve resmî tatillerde
- Şantiyeye izinsiz giriş; çocukların açık kata çıkması ciddi bir can güvenliği riskidir
- Taşeron ve yüklenici personelin kimlik kaydı, iş güvenliği ekipmanı kontrolü
- Malzeme çıkışının irsaliye ile eşleştirilmesi

Şantiyelerde görevli sayısından çok **aydınlatma, çevre çiti ve kamera yerleşimi** belirleyicidir. Keşifte bunları birlikte değerlendiriyoruz.

## Teslim sonrası site güvenliği

Yeni teslim edilen sitelerde ilk 6 ay kritiktir: tadilat yapan daire sahipleri, mobilya teslimatları, taşınma trafiği ve henüz oturmamış bir yönetim düzeni aynı anda gelir. Bu dönemde önerdiğimiz uygulamalar:

- Taşınma ve tadilat için **randevulu giriş** sistemi; asansör ve koridor koruma planı
- Yüklenici araçları için ayrı giriş saati tanımı
- Yüksek katlı bloklarda yangın merdiveni ve kaçış yolu kontrol turu
- Henüz devreye alınmamış kamera/kartlı geçiş sistemleri için geçici manuel prosedür

## Yüksek katlı yapılarda dikkat edilenler

Kartal'daki yeni rezidansların çoğu yüksek katlı. Bu yapılarda güvenlik operasyonu; asansör arızası, elektrik kesintisi ve yangın senaryolarına göre ayrıca planlanmalıdır. Görevlinin en üst kata ulaşma süresi, acil durum planının en temel verisidir.

Şantiyeden oturuma geçişte [tesis yönetimi](/hizmet/tesis-yonetimi) hizmetini de devraldığımızda, güvenlik ekibi ile teknik ekip arasındaki bilgi akışı kopmuyor.`,
    faqs: [
      {
        q: "Şantiyemiz için sadece gece güvenliği alabilir miyiz?",
        a: "Evet, şantiyelerde en sık tercih edilen model bu. Gündüz saha zaten kalabalık olduğu için gece ve hafta sonu ağırlıklı bir düzen kuruluyor. Resmî tatiller ayrıca planlanır; malzeme kaybının en çok yaşandığı dönem uzun tatillerdir.",
      },
      {
        q: "İnşaat bitince aynı ekip site güvenliğine geçebilir mi?",
        a: "Geçiş planlanabilir ve genellikle avantajlıdır — sahayı tanıyan ekip devam eder. Ancak görev tanımı tamamen değişir; bu nedenle personel yeniden eğitilir ve yeni vardiya planı hazırlanır.",
      },
      {
        q: "Kentsel dönüşümde boş bina güvenliği yapıyor musunuz?",
        a: "Evet. Tahliye edilmiş ancak yıkımı başlamamış binalarda izinsiz giriş ve işgal riski oluşur. Bu dönem için devriye ağırlıklı, çoğunlukla çevre kamera desteğiyle kurgulanan ekonomik bir model uygulanıyor.",
      },
    ],
  },
  {
    hizmet: "koruma-ve-guvenlik",
    bolge: "pendik",
    bolgeAdi: "Pendik",
    title: "Pendik Özel Güvenlik Firması",
    description:
      "Pendik'te lojistik, sanayi ve site güvenliği. Sabiha Gökçen ve Kurtköy çevresinde 7/24 vardiyalı operasyon. Ücretsiz keşif: 0216 222 11 44.",
    h1: "Pendik Özel Güvenlik Hizmetleri",
    lede:
      "Pendik; havalimanı, sanayi bölgeleri, marina ve hızla büyüyen konut alanlarıyla Anadolu Yakası'nın en çeşitli güvenlik ihtiyacına sahip ilçelerinden biri.",
    body: `Pendik'te güvenlik operasyonlarının belirleyici özelliği **süreklilik**: havalimanı çevresindeki işletmeler, lojistik depoları ve üretim tesisleri gece-gündüz ayrımı olmadan çalışır. Bu da güvenlik hizmetinin gerçek anlamda 7/24 kurgulanmasını gerektirir.

## Lojistik ve depo tesisleri

Kurtköy ve çevresindeki lojistik tesislerde odak noktalarımız:

- **Kapı kaydının eksiksizliği:** Araç plakası, sürücü kimliği, dorse mühür numarası ve çıkış saati. Bu dört veri tutulmadığında kayıp analizinde geriye dönülemez.
- **Rampada bekleme yönetimi:** Yükleme sırası bekleyen araçların park düzeni hem güvenlik hem iş güvenliği konusudur.
- **Gece sevkiyatı:** Gece saatlerinde tesise giren araç sayısı azalır ama risk artar; bu saatlerde çift kontrol uygulanması önerilir.
- **Personel çıkışı:** Depo tesislerinde vardiya çıkışlarında yapılan kontrollerin, çalışan haklarını ihlal etmeyecek şekilde ve yazılı prosedürle yürütülmesi gerekir.

## Havalimanı çevresi işletmeler

Sabiha Gökçen çevresindeki otel, ofis ve hizmet işletmelerinde 24 saat esasına göre çalışan bir müşteri profili var. Buradaki güvenlik hizmeti, gece saatlerinde de **kurumsal karşılama** niteliğini korumak zorunda: görevli hem kontrol eden hem yönlendiren kişi konumunda.

## Konut projeleri

Pendik'in yeni konut alanlarında site güvenliği talebi hızla artıyor. Geniş yerleşkelerde tek kapıdan kontrol yeterli olmuyor; ikincil kapılar, yaya geçişleri ve otopark rampaları ayrı ayrı değerlendiriliyor.

## Vardiya ve ulaşım

7/24 çalışan tesislerde en büyük operasyonel risk, gece vardiyasına ulaşım kaynaklı devamsızlıktır. Pendik ve çevre ilçelerden işe alım yaparak bu riski azaltıyor, vardiya devir saatlerini toplu taşıma saatleriyle uyumlu planlıyoruz.

Tesis genelinde [ilaçlama](/hizmet/ilaclama) ve [genel temizlik](/hizmet/genel-temizlik) hizmetleri aynı sözleşmeye eklenebilir.`,
    faqs: [
      {
        q: "Deponuzda gece sevkiyatı var, güvenlik gece de aynı kadroda mı çalışır?",
        a: "Kadro sayısı sevkiyat yoğunluğuna göre belirlenir. Gece araç trafiği azalsa da risk arttığı için genellikle kapı kontrolünün korunması, devriye sıklığının ise artırılması öneriliyor.",
      },
      {
        q: "Havalimanına yakın olmamız güvenlik prosedürünü değiştirir mi?",
        a: "Doğrudan bir mevzuat farkı yaratmaz; ancak 24 saat açık işletme profili vardiya planını ve personel niteliğini etkiler. Gece saatlerinde de kurumsal karşılama yapabilecek görevli tercih edilir.",
      },
      {
        q: "Birden fazla tesisimiz var, tek sözleşme yapılabilir mi?",
        a: "Evet. Aynı ilçede veya farklı ilçelerde birden fazla tesis için tek çerçeve sözleşme ve birleşik raporlama sunuyoruz. Her tesisin kendi vardiya planı olur, raporlama tek belgede toplanır.",
      },
    ],
  },
  {
    hizmet: "koruma-ve-guvenlik",
    bolge: "uskudar",
    bolgeAdi: "Üsküdar",
    title: "Üsküdar Özel Güvenlik Firması",
    description:
      "Üsküdar'da kurum, hastane, iş merkezi ve site güvenliği. Altunizade çevresinde ofis, tarihi dokuda apartman güvenliği. Ücretsiz keşif: 0216 222 11 44.",
    h1: "Üsküdar Özel Güvenlik Hizmetleri",
    lede:
      "Üsküdar; kamu kurumları, hastaneler, iş merkezleri ve tarihi konut dokusunun iç içe geçtiği, yoğun toplu taşıma trafiğine sahip bir ilçe.",
    body: `Üsküdar'daki güvenlik projelerinde en belirleyici unsur, **kamuya açıklık derecesi**. Hastane, kamu binası ve iş merkezi gibi herkesin girebildiği yapılarda güvenlik, kapıyı kapatmak üzerine değil, akışı yönetmek üzerine kurulur.

## Sağlık kuruluşlarında güvenlik

Hastane ve poliklinik güvenliği ayrı bir uzmanlık alanıdır:

- **Danışma ve yönlendirme yükü:** Görevlinin zamanının önemli bölümü yön tarifiyle geçer; bu görev tanımına yazılmalı ve personel buna göre eğitilmelidir.
- **Refakatçi ve ziyaret saatleri:** Ziyaret saati dışındaki girişlerin yönetimi, en sık tartışma yaşanan konudur. Prosedürün yazılı ve tabelalı olması, görevliyi tartışmanın ortasında bırakmaz.
- **Acil servis:** En yüksek gerilim noktası burasıdır. Acil serviste görevlendirilecek personelde sakinleştirici iletişim becerisi öne çıkar.
- **İlaç ve tıbbi malzeme depoları:** Erişim yetkisi sınırlı alanların kayıt altına alınması gerekir.

## Altunizade ve iş merkezleri

Altunizade çevresindeki ofis ve iş merkezlerinde plaza tipi lobi operasyonu geçerlidir: ziyaretçi kaydı, turnike yönetimi, kargo kabul noktası ve mesai dışı erişim kontrolü.

## Tarihi doku ve dar sokaklar

Üsküdar'ın eski mahallelerinde araçla devriye çoğu zaman mümkün değildir; yaya devriye rotası, dar sokak ve merdivenli geçişlere göre planlanır. Tarihi yapılarda kamera ve kablolama için yapı üzerinde tadilat kısıtları olabilir; bu durumda kablosuz çözümler ve personel ağırlıklı kurgu tercih edilir.

## Toplu taşıma yoğunluğu

Meydan ve iskele çevresindeki işletmelerde sabah ve akşam saatlerindeki yoğunluk, mağaza ve şube güvenliğinin kritik saatlerini belirler. Vardiya planı bu saatlere göre ağırlıklandırılır.

Kurumsal projelerde [genel temizlik](/hizmet/genel-temizlik) ve [tesis yönetimi](/hizmet/tesis-yonetimi) hizmetleri aynı sözleşmede birleştirilebilir.`,
    faqs: [
      {
        q: "Hastane ve sağlık kuruluşlarında deneyiminiz var mı?",
        a: "Evet, sağlık tesislerinde güvenlik hizmeti veriyoruz. Bu projelerde personel seçiminde iletişim becerisi ve kriz anında sakin kalabilme öne çıkar; görev öncesi ayrıca hasta ve refakatçi ilişkileri eğitimi veriliyor.",
      },
      {
        q: "Tarihi binamıza kamera takılamıyor, ne yapabiliriz?",
        a: "Yapı üzerinde delme ve kablolama kısıtı olan binalarda kablosuz kamera, bataryalı çözümler ve mevcut tesisata müdahale etmeyen yerleşimler kullanılıyor. Bu tür yapılarda güvenliğin ağırlığı doğal olarak personel tarafına kayar.",
      },
      {
        q: "Kamu kurumları için ihaleye giriyor musunuz?",
        a: "Evet, kamu ihalelerinde teklif veriyoruz. İhale şartnamesindeki personel sayısı, nitelik ve belge gereklilikleri doğrultusunda dosya hazırlanıyor.",
      },
    ],
  },
];

/** Bir hizmetin tüm bölge sayfaları */
export function getLocalPagesForService(hizmet: string): LocalPage[] {
  return LOCAL_PAGES.filter((p) => p.hizmet === hizmet);
}

/** Tek sayfa getir */
export function getLocalPage(hizmet?: string, bolge?: string): LocalPage | undefined {
  if (!hizmet || !bolge) return undefined;
  return LOCAL_PAGES.find((p) => p.hizmet === hizmet && p.bolge === bolge);
}

/** sitemap ve iç linkleme için tüm yollar */
export function localPagePaths(): string[] {
  return LOCAL_PAGES.map((p) => `hizmet/${p.hizmet}/${p.bolge}`);
}
